<template>
  <Drawer
    v-model:visible="visible"
    position="right"
    :style="drawerStyle"
    :pt="{
      header: '',
      content: 'flex flex-col h-full min-h-0 overflow-hidden p-0',
      transition: {
        enterFromClass: '',
        enterActiveClass: '',
        enterToClass: '',
        leaveFromClass: '',
        leaveActiveClass: '',
        leaveToClass: '',
      },
    }"
    class="h-full relative"
    data-test-id="shopify-payouts-drawer"
  >
    <template #header>
      <div class="flex items-center justify-between w-full pr-4">
        <span>{{ drawerHeader }}</span>
        <Button
          :icon="
            isFullscreen ? 'pi pi-window-minimize' : 'pi pi-window-maximize'
          "
          variant="text"
          size="small"
          @click="toggleFullscreen"
          :aria-label="isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'"
          data-test-id="fullscreen-toggle-btn"
        />
      </div>
    </template>
    <div class="h-full flex flex-col min-h-0">
      <!-- Back button when viewing payout detail or balance transaction detail -->
      <div
        v-if="store.selectedPayout || isBalanceTransactionDetailRoute"
        class="px-4 bg-surface-0 dark:bg-surface-900"
      >
        <Button
          v-if="isBalanceTransactionDetailRoute"
          label="Back to Payout Detail"
          icon="pi pi-arrow-left"
          @click="clearSelectedBalanceTransaction"
          variant="text"
          size="small"
          data-test-id="back-to-payout-detail-btn"
        />
        <Button
          v-else-if="store.selectedPayout"
          :label="
            cameFromPendingPayouts
              ? 'Back to Pending Payouts List'
              : 'Back to Payouts List'
          "
          icon="pi pi-arrow-left"
          @click="clearSelectedPayout"
          variant="text"
          size="small"
          data-test-id="back-to-payouts-btn"
        />
      </div>

      <!-- Payouts List View -->
      <div
        v-if="!isPayoutDetailRoute"
        class="flex-1 overflow-y-auto p-4"
        data-test-id="payouts-list-container"
      >
        <div class="space-y-4">
          <!-- Header Section -->
          <div
            class="bg-surface-0 dark:bg-surface-900 rounded-lg border border-surface-200 dark:border-surface-700 p-4"
            data-test-id="payouts-header"
          >
            <div class="flex items-center justify-between gap-4">
              <div>
                <h3 class="text-lg font-semibold text-surface-900 dark:text-surface-0">
                  {{
                    isPendingPayoutsRoute
                      ? 'Payouts Pending Bank Account Assignment'
                      : 'Shopify Payouts'
                  }}
                </h3>
                <p class="text-sm text-surface-600 dark:text-surface-300 mt-1">
                  {{
                    isPendingPayoutsRoute
                      ? 'Payouts across all merchants that need manual bank account assignment'
                      : 'View and manage payouts for this merchant'
                  }}
                </p>
              </div>
              <div class="flex items-center gap-3">
                <Select
                  filter
                  v-model="selectedMerchantIdLocal"
                  :options="merchantOptions"
                  optionLabel="Name"
                  optionValue="Id"
                  placeholder="Select merchant or Pending"
                  :showClear="false"
                  class="w-80"
                  data-test-id="payouts-merchant-filter"
                  size="small"
                  @change="handleMerchantChange"
                />
                <Button
                  v-if="isPendingPayoutsRoute"
                  :icon="isSubscribed ? 'pi pi-bell-slash' : 'pi pi-bell'"
                  :label="isSubscribed ? 'Unsubscribe' : 'Subscribe'"
                  :variant="isSubscribed ? 'outlined' : 'primary'"
                  size="small"
                  @click="handleToggleSubscription"
                  :loading="isToggling"
                  :disabled="isToggling || testingNotification"
                  v-tooltip.top="
                    isSubscribed
                      ? 'Unsubscribe from Slack notifications'
                      : 'Subscribe to Slack notifications'
                  "
                  data-test-id="notification-subscribe-btn"
                />
                <Button
                  v-if="isPendingPayoutsRoute"
                  label="Send Test"
                  icon="pi pi-send"
                  variant="outlined"
                  size="small"
                  @click="handleSendTest"
                  :loading="testingNotification"
                  :disabled="!isSubscribed || isToggling || testingNotification"
                  v-tooltip.top="'Send a test Slack notification'"
                  data-test-id="notification-test-btn"
                />
                <Button
                  label="Refresh"
                  icon="pi pi-refresh"
                  variant="outlined"
                  size="small"
                  @click="handleRefresh"
                  :loading="store.isLoading"
                  data-test-id="payouts-refresh-btn"
                />
              </div>
            </div>
          </div>

          <!-- Payouts Table -->
          <div class="bg-surface-0 dark:bg-surface-900">
            <PayoutsTable
              v-if="!isPendingPayoutsRoute"
              ref="payoutsTableRef"
              :key="`payouts-table-${effectiveMerchantId || 'none'}`"
              :merchant-id="effectiveMerchantId"
              :initial-store-id="routeStoreId"
              @row-select="handleRowSelect"
            />
            <PayoutsTable
              v-else
              ref="payoutsTableRef"
              :merchant-id="null"
              :show-merchant-column="true"
              :use-pending-payouts="true"
              :show-filters="false"
              @row-select="handleRowSelect"
            />
          </div>
        </div>
      </div>

      <!-- Loading state for payout detail direct access -->
      <div
        v-else-if="
          isPayoutDetailRoute &&
          !isBalanceTransactionDetailRoute &&
          !store.selectedPayout
        "
        class="flex-1 overflow-y-auto p-4 flex items-center justify-center"
        data-test-id="payout-detail-loading"
      >
        <LoadingSpinner :is-loading="true">
          <template #loading-text>Loading payout...</template>
        </LoadingSpinner>
      </div>

      <!-- Payout Detail View -->
      <div
        v-else-if="store.selectedPayout && !isBalanceTransactionDetailRoute"
        class="flex-1 overflow-y-auto p-4"
        data-test-id="payout-detail-container"
      >
        <PayoutDetailContent
          :merchant-id="effectiveMerchantId"
          :payout="store.selectedPayout"
          @back="clearSelectedPayout"
          @select-balance-transaction="handleSelectBalanceTransaction"
        />
      </div>

      <!-- Loading State for Balance Transaction Detail -->
      <div
        v-else-if="
          isBalanceTransactionDetailRoute && !store.selectedPayoutDetail
        "
        class="flex-1 overflow-y-auto p-4 flex items-center justify-center"
        data-test-id="balance-transaction-loading"
      >
        <LoadingSpinner :is-loading="true">
          <template #loading-text>Loading payout details...</template>
        </LoadingSpinner>
      </div>

      <!-- Balance Transaction Detail View -->
      <div
        v-else-if="
          isBalanceTransactionDetailRoute && store.selectedPayoutDetail
        "
        class="flex-1 overflow-y-auto p-4"
        data-test-id="balance-transaction-detail-container"
      >
        <BalanceTransactionDetailContent
          :merchant-id="effectiveMerchantId"
          :store-id="store.selectedPayoutDetail.StoreId"
          :business-entity-id="store.selectedPayoutDetail.BusinessEntityId"
          :payout-id="store.selectedPayoutDetail.Id"
          :balance-transaction="store.selectedBalanceTransaction"
          :transaction-id="
            routeTransactionId ? parseInt(routeTransactionId, 10) : null
          "
        />
      </div>
    </div>
  </Drawer>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useFrontegg } from '@frontegg/vue'
import { useToast } from 'primevue/usetoast'
import Drawer from 'primevue/drawer'
import Button from 'primevue/button'
import Select from 'primevue/select'
import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
import PayoutsTable from '@/components/shopifypayouts/PayoutsTable.vue'
import PayoutDetailContent from '@/components/shopifypayouts/PayoutDetailContent.vue'
import BalanceTransactionDetailContent from '@/components/shopifypayouts/BalanceTransactionDetailContent.vue'
import { useShopifyPayoutsStore } from '@/stores/shopifyPayoutsStore'
import { useMerchantStore } from '@/stores/merchantStore'
import { shopifyPayoutsEmbeddedApi } from '@/services/shopifyPayoutsEmbeddedService'
import { slackNotificationApi } from '@/services/slackNotificationService'
import { successToast, warnToast, errorToast, errorToastWithDetails } from '@/util/toasts'
import { normalizeError } from '@/util/errorNormalizer'
import { ApiError } from '@/util/ApiError'
import type { SlackSubscriptionResponse } from '@/types/slackNotifications'
import type {
  ShopifyPayoutSummary,
  ShopifyPayoutDetail,
  ShopifyBalanceTransactionSummary,
} from '@/types/shopifyPayouts'

interface Props {
  visible: boolean
  merchantId: string
  merchantName?: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:visible': [value: boolean]
}>()

const route = useRoute()
const router = useRouter()
const toast = useToast()
const { authState } = useFrontegg()
const store = useShopifyPayoutsStore()
const merchantStore = useMerchantStore()

// Component refs
const payoutsTableRef = ref<InstanceType<typeof PayoutsTable> | null>(null)

// Local state
const selectedMerchantIdLocal = ref<string | null>(props.merchantId || null)
const isFullscreen = ref(false)
const isDirectRouteLoading = ref(false)
const isNavigatingBetweenPayouts = ref(false)
const cameFromPendingPayouts = ref(false)
let pendingDirectLoad: Promise<ShopifyPayoutSummary | null> | null = null

// Notification state
const subscription = ref<SlackSubscriptionResponse | null>(null)
// One subscription fetch per drawer session — the visible watcher and
// onMounted must not each fire their own, which showed up as 404 noise in RUM
const subscriptionLoaded = ref(false)
const isToggling = ref(false)
const testingNotification = ref(false)
const isSubscribed = computed(() => subscription.value?.IsActive ?? false)

// Route helpers
const routeName = computed(() => route.name as string)
const routePayoutId = computed(
  () => route.params.payoutId as string | undefined,
)
const routeTransactionId = computed(
  () => route.params.transactionId as string | undefined,
)
const merchantRouteId = computed(() => route.params.id as string | undefined)
const routeStoreId = computed(
  () => (route.params.storeId as string | undefined) || null,
)
// The owning business entity is a required path segment on the payout-detail
// and balance-transaction routes (`business-entity/:businessEntityId`), so the
// payout resolves in a single API call. Absent on the payouts list and stores
// routes, where it's not needed.
const routeBusinessEntityId = computed(
  () => (route.params.businessEntityId as string | undefined) || null,
)

const isPayoutsRoute = computed(() =>
  [
    'shopifyStoresPayouts',
    'shopifyStoresPayoutDetail',
    'shopifyStoresBalanceTransactionDetail',
    'merchantManagementPayoutsPending',
  ].includes(routeName.value),
)

const isPendingPayoutsRoute = computed(
  () => routeName.value === 'merchantManagementPayoutsPending',
)

const isPayoutDetailRoute = computed(() =>
  [
    'shopifyStoresPayoutDetail',
    'shopifyStoresBalanceTransactionDetail',
  ].includes(routeName.value),
)

const isBalanceTransactionDetailRoute = computed(
  () => routeName.value === 'shopifyStoresBalanceTransactionDetail',
)

const visible = computed({
  get: () =>
    props.visible || isPayoutsRoute.value || isNavigatingBetweenPayouts.value,
  set: (value: boolean) => {
    emit('update:visible', value)
    if (!value && isPayoutsRoute.value) {
      // Defer route navigation to next frame so the drawer close animation
      // isn't blocked by heavy DOM teardown (improves INP by ~200ms)
      requestAnimationFrame(() => {
        if (isPendingPayoutsRoute.value) {
          router.push({ name: 'merchantManagement' })
        } else if (merchantRouteId.value) {
          router.push({
            name: 'merchantConfigurationShopifyStores',
            params: { id: merchantRouteId.value },
          })
        }
      })
    }
  },
})

const effectiveMerchantId = computed(
  () => store.effectiveMerchantId || props.merchantId,
)

// Filter merchants to only show those with Shopify stores, and add "Pending" option
const merchantOptions = computed(() => {
  const allMerchants = merchantStore.getMerchants()
  const merchantsWithShopifyStores = allMerchants.filter(
    merchant => merchant.ShopifyStores && merchant.ShopifyStores.length > 0,
  )

  return [
    { Id: '__PENDING__', Name: 'Pending', ShopifyStores: [] },
    ...merchantsWithShopifyStores,
  ]
})

const drawerHeader = computed(() => {
  if (store.selectedBalanceTransaction) {
    return `Balance Transaction ${store.selectedBalanceTransaction.Id}`
  }
  if (store.selectedPayout) {
    return `Payout ${store.selectedPayout.Id} Details`
  }
  const selectedMerchant = merchantOptions.value.find(
    m => m.Id === effectiveMerchantId.value,
  )
  return selectedMerchant
    ? `Shopify Payouts - ${selectedMerchant.Name}`
    : 'Shopify Payouts'
})

const drawerStyle = computed(() => {
  if (isFullscreen.value) {
    return { width: '100vw', maxWidth: '100vw' }
  }
  return { width: '90vw', maxWidth: '1600px' }
})

function toggleFullscreen() {
  isFullscreen.value = !isFullscreen.value
}

// Navigation helpers
function navigateToRoute(
  routeName: string,
  params: Record<string, string | number> = {},
  query?: Record<string, string>,
) {
  if (!merchantRouteId.value) return
  router.push({
    name: routeName,
    params: { id: merchantRouteId.value, ...params },
    ...(query ? { query } : {}),
  })
}

function navigateToPayoutDetail(payout: ShopifyPayoutSummary) {
  navigateToRoute('shopifyStoresPayoutDetail', {
    storeId: payout.StoreId,
    businessEntityId: payout.BusinessEntityId,
    payoutId: payout.Id.toString(),
  })
}

function navigateToPayoutsList() {
  navigateToRoute('shopifyStoresPayouts')
}

function navigateToBalanceTransactionDetail(
  payoutId: string | number,
  transactionId: string | number,
  businessEntityId?: string | null,
  storeId?: string | null,
) {
  // The BT route inherits the payout's `storeId` and `businessEntityId` path
  // segments. Prefer the values passed by the caller (from the resolved payout
  // detail); fall back to the current route's segments. Both are required, so
  // bail if neither source has them.
  const entityId = businessEntityId || routeBusinessEntityId.value
  const resolvedStoreId = storeId || routeStoreId.value
  if (!entityId || !resolvedStoreId) return
  navigateToRoute('shopifyStoresBalanceTransactionDetail', {
    storeId: resolvedStoreId,
    businessEntityId: entityId,
    payoutId: payoutId.toString(),
    transactionId: transactionId.toString(),
  })
}

function navigateBackToPayoutDetail() {
  if (routePayoutId.value) {
    const routeConfig: {
      name: string
      params: Record<string, string>
      query?: Record<string, string>
    } = {
      name: 'shopifyStoresPayoutDetail',
      params: {
        id: merchantRouteId.value!,
        // Preserve the store + business-entity path segments so the
        // payout-detail route still resolves after backing out of a balance
        // transaction. Both are always present on the BT route (its path
        // includes `business-entity/:businessEntityId`), so the conditional
        // spread is belt-and-suspenders — it just avoids passing an `undefined`
        // param, not a real "segment missing" case.
        ...(routeStoreId.value ? { storeId: routeStoreId.value } : {}),
        ...(routeBusinessEntityId.value
          ? { businessEntityId: routeBusinessEntityId.value }
          : {}),
        payoutId: routePayoutId.value,
      },
    }

    if (route.query.fromPending === 'true') {
      routeConfig.query = { fromPending: 'true' }
    }

    router.push(routeConfig)
  }
}

// Error handling helper
function handleApiError(error: unknown) {
  const apiError = normalizeError(error)
  errorToastWithDetails(toast, 'Error', apiError.toToastMessage(), apiError)
}

// A payout detail returned by the API isn't a list summary, but it carries the
// fields the summary-driven UI needs. Project it down to a summary shape.
function payoutSummaryFromDetail(
  detail: ShopifyPayoutDetail,
): ShopifyPayoutSummary {
  return {
    Id: detail.Id,
    StoreId: detail.StoreId,
    BankAccountId: detail.BankAccountId,
    BusinessEntityId: detail.BusinessEntityId,
    Amount: detail.Amount,
    Currency: detail.Currency,
    Status: detail.Status,
    IssuedAt: detail.IssuedAt,
    TransactionType: detail.TransactionType,
  }
}

// The payout-detail route carries the owning store and business entity as path
// segments, so the payout resolves in a single API call. Returns null when the
// id isn't numeric or the named entity doesn't own the payout
// (ENTITY_NOT_FOUND) — the caller treats that as "not found" (there is no
// brute-force fallback; the URL is expected to name the owning entity).
async function fetchPayoutViaDirectLookup(
  payoutId: string,
  storeId: string,
  businessEntityId: string,
): Promise<ShopifyPayoutSummary | null> {
  if (!effectiveMerchantId.value) {
    return null
  }

  const payoutNumericId = Number(payoutId)
  if (!Number.isFinite(payoutNumericId)) {
    return null
  }

  try {
    const detail = await shopifyPayoutsEmbeddedApi.getPayoutDetail(
      authState,
      effectiveMerchantId.value,
      storeId,
      businessEntityId,
      payoutNumericId,
    )
    // Store the detail AND prime the load cache (keyed on the store + entity +
    // payout tuple), so the route watchers and mount handler that call
    // `store.loadPayoutDetail` for this payout short-circuit on that cache
    // instead of re-fetching the detail we just resolved.
    store.primeLoadedDetail(detail)
    return payoutSummaryFromDetail(detail)
  } catch (error) {
    if (error instanceof ApiError && error.code === 'ENTITY_NOT_FOUND') {
      return null
    }
    throw error
  }
}

async function fetchPayoutForRoute(
  payoutId: string,
): Promise<ShopifyPayoutSummary | null> {
  if (!effectiveMerchantId.value) {
    return null
  }

  if (store.selectedPayout?.Id.toString() === payoutId) {
    return store.selectedPayout
  }

  if (pendingDirectLoad) {
    await pendingDirectLoad
    return store.selectedPayout?.Id.toString() === payoutId
      ? store.selectedPayout
      : store.findPayoutById(payoutId)
  }

  pendingDirectLoad = (async () => {
    isDirectRouteLoading.value = true
    try {
      let payout = store.findPayoutById(payoutId)

      // The payout-detail route carries the owning store + business entity as
      // path segments, so resolve the payout in a single API call when it isn't
      // already loaded in the store. The direct lookup is the only resolution
      // path, so a link missing either segment can't be resolved at all —
      // surface that to the user instead of leaving an empty panel.
      if (!payout) {
        if (!routeStoreId.value || !routeBusinessEntityId.value) {
          errorToast(
            toast,
            'Cannot open payout',
            'This payout link is missing its store or business entity and cannot be opened. Please navigate to the payout from the payouts list.',
            'notification-error',
          )
          return null
        }
        try {
          payout = await fetchPayoutViaDirectLookup(
            payoutId,
            routeStoreId.value,
            routeBusinessEntityId.value,
          )
        } catch (error) {
          handleApiError(error)
          return null
        }
      }

      if (!payout) {
        // Resolution ran but the payout doesn't exist for this store/entity.
        errorToast(
          toast,
          'Payout not found',
          `Payout ${payoutId} could not be found for this store and business entity.`,
          'notification-error',
        )
        return null
      }

      store.setSelectedPayout(payout)
      return payout
    } catch (error) {
      handleApiError(error)
      return null
    } finally {
      isDirectRouteLoading.value = false
    }
  })()

  try {
    return await pendingDirectLoad
  } finally {
    pendingDirectLoad = null
  }
}

async function loadPayoutDetailForRoute(payoutId: string): Promise<boolean> {
  const payout = await fetchPayoutForRoute(payoutId)
  if (!payout || !effectiveMerchantId.value) {
    return false
  }

  try {
    await store.loadPayoutDetail(
      authState,
      effectiveMerchantId.value,
      payout.StoreId,
      payout.BusinessEntityId,
      payout.Id,
    )
    return true
  } catch (error) {
    handleApiError(error)
    return false
  }
}

// Load payout detail for balance transaction view (needed for direct URL access)
async function loadPayoutDetailForTransaction() {
  if (
    !isBalanceTransactionDetailRoute.value ||
    !routePayoutId.value ||
    !effectiveMerchantId.value
  ) {
    return
  }

  await loadPayoutDetailForRoute(routePayoutId.value)
}

function clearSelectionState() {
  store.clearSelection()
}

// Event handlers
function handleRefresh() {
  payoutsTableRef.value?.refresh()
}

async function loadSubscription() {
  try {
    subscription.value = await slackNotificationApi.getSubscription(authState)
  } catch (err: unknown) {
    // Silently fail - subscription status is optional
    console.error('Failed to load subscription status:', err)
  }
}

async function handleToggleSubscription() {
  if (isToggling.value) return

  isToggling.value = true
  const previousSubscription = subscription.value

  try {
    if (isSubscribed.value) {
      // Unsubscribe
      await slackNotificationApi.unsubscribe(authState)
      subscription.value = null
      successToast(
        toast,
        'Unsubscribed',
        'You will no longer receive Slack notifications',
        'unsubscribe-success',
      )
    } else {
      // Subscribe
      subscription.value = await slackNotificationApi.subscribe(authState)
      successToast(
        toast,
        'Subscribed',
        'You will now receive daily Slack notifications',
        'subscribe-success',
      )
    }
  } catch (err: unknown) {
    handleNotificationError(err)
    // Revert subscription state on error
    subscription.value = previousSubscription
  } finally {
    isToggling.value = false
  }
}

async function handleSendTest() {
  if (testingNotification.value) return

  testingNotification.value = true

  try {
    const result = await slackNotificationApi.sendTestNotification(authState)
    if (result.Sent) {
      successToast(
        toast,
        'Test Sent',
        `Test notification sent! (${result.PayoutCount} pending payout${result.PayoutCount !== 1 ? 's' : ''})`,
        'test-notification-success',
      )
    } else {
      errorToast(
        toast,
        'Test Failed',
        'Failed to send test notification',
        'test-notification-error',
      )
    }
  } catch (err: unknown) {
    handleNotificationError(err)
  } finally {
    testingNotification.value = false
  }
}

function handleNotificationError(err: unknown) {
  if (err && typeof err === 'object' && 'Code' in err) {
    const errorCode = (err as { Code: string }).Code
    if (errorCode === 'SLACK_USER_NOT_FOUND') {
      warnToast(
        toast,
        'User Not Found',
        'Your email was not found in the Slack workspace. Please contact your administrator.',
        'notification-warning',
      )
    } else if (errorCode === 'SLACK_SERVICE_ERROR') {
      errorToast(
        toast,
        'Error',
        'Slack service is temporarily unavailable. Please try again later.',
        'notification-error',
      )
    } else {
      const message =
        (err as { Message?: string }).Message || 'An unexpected error occurred'
      errorToast(toast, 'Error', message, 'notification-error')
    }
  } else {
    const apiError = normalizeError(err)
    errorToastWithDetails(toast, 'Error', apiError.toToastMessage(), apiError)
  }
}

async function handleMerchantChange() {
  const merchantId = selectedMerchantIdLocal.value

  if (merchantId === '__PENDING__') {
    isNavigatingBetweenPayouts.value = true
    await router.push({ name: 'merchantManagementPayoutsPending' })
    await nextTick()
    isNavigatingBetweenPayouts.value = false
    return
  }

  if (isPendingPayoutsRoute.value && merchantId) {
    isNavigatingBetweenPayouts.value = true
    await router.push({
      name: 'shopifyStoresPayouts',
      params: { id: merchantId },
    })
    await nextTick()
    isNavigatingBetweenPayouts.value = false
    return
  }

  store.syncMerchantId(merchantId, true)
}

function handleRowSelect(payout: ShopifyPayoutSummary) {
  if (isPendingPayoutsRoute.value && payout.MerchantId) {
    // storeId + businessEntityId are required path segments on the detail
    // route. The pending list is a cross-merchant aggregate and a row may not
    // carry both — without them vue-router builds a malformed URL (`.../store//`)
    // and `router.push` silently no-ops, so guard and tell the user instead.
    if (!payout.StoreId || !payout.BusinessEntityId) {
      errorToast(
        toast,
        'Cannot open payout',
        'This pending payout is missing its store or business entity and cannot be opened yet.',
        'notification-error',
      )
      return
    }
    cameFromPendingPayouts.value = true
    // Sync merchant ID BEFORE setting payout — syncMerchantId calls
    // clearSelection() when merchant changes, which would null out the payout
    store.syncMerchantId(payout.MerchantId)
    store.setSelectedPayout(payout)
    router.push({
      name: 'shopifyStoresPayoutDetail',
      params: {
        id: payout.MerchantId,
        storeId: payout.StoreId,
        businessEntityId: payout.BusinessEntityId,
        payoutId: payout.Id.toString(),
      },
      query: {
        fromPending: 'true',
      },
    })
  } else {
    store.setSelectedPayout(payout)
    cameFromPendingPayouts.value = false
    navigateToPayoutDetail(payout)
  }
}

function clearSelectedPayout() {
  // Defer store cleanup to the next frame so the click handler returns quickly.
  // This prevents the 440ms+ long task caused by synchronous DOM teardown
  // during the BUTTON.onclick event (observed in RUM long_task data).
  requestAnimationFrame(() => {
    clearSelectionState()

    if (cameFromPendingPayouts.value) {
      cameFromPendingPayouts.value = false
      router.push({
        name: 'merchantManagementPayoutsPending',
        query: {},
      })
    } else {
      navigateToPayoutsList()
    }
  })
}

function handleSelectBalanceTransaction(
  transaction: ShopifyBalanceTransactionSummary,
  payoutDetail: ShopifyPayoutDetail,
) {
  store.setSelectedBalanceTransaction(transaction)
  store.setSelectedPayoutDetail(payoutDetail)
  if (store.selectedPayout) {
    navigateToBalanceTransactionDetail(
      store.selectedPayout.Id,
      transaction.Id,
      payoutDetail.BusinessEntityId,
      payoutDetail.StoreId,
    )
  }
}

function clearSelectedBalanceTransaction() {
  const payoutToPreserve = store.selectedPayout

  // Defer heavy state + route updates to the next frame to avoid blocking the click handler
  requestAnimationFrame(async () => {
    store.setSelectedBalanceTransaction(null)

    navigateBackToPayoutDetail()

    await nextTick()
    if (payoutToPreserve && routePayoutId.value) {
      if (
        !store.selectedPayout &&
        payoutToPreserve.Id.toString() === routePayoutId.value
      ) {
        store.setSelectedPayout(payoutToPreserve)
      }
    }
  })
}

// Watchers
watch(
  () => routePayoutId.value,
  async (payoutId, oldPayoutId) => {
    if (payoutId !== oldPayoutId) {
      store.resetPayoutDetailCache()
    }

    if (!payoutId && isPayoutsRoute.value) {
      clearSelectionState()
      return
    }

    if (!payoutId) return

    if (isPayoutDetailRoute.value && !isBalanceTransactionDetailRoute.value) {
      await loadPayoutDetailForRoute(payoutId)
    } else if (isBalanceTransactionDetailRoute.value) {
      await loadPayoutDetailForTransaction()
    }
  },
  { immediate: true },
)

watch(
  () => routeTransactionId.value,
  async transactionId => {
    if (transactionId && isBalanceTransactionDetailRoute.value) {
      if (!store.selectedPayoutDetail && routePayoutId.value) {
        await loadPayoutDetailForTransaction()
      }
    } else if (!transactionId && !isBalanceTransactionDetailRoute.value) {
      store.setSelectedBalanceTransaction(null)
    }
  },
  { immediate: true },
)

watch(
  () => props.visible,
  async isVisible => {
    if (isVisible && !subscriptionLoaded.value) {
      // Load subscription status once per drawer session
      subscriptionLoaded.value = true
      await loadSubscription()
    }

    if (!isVisible) return

    if (!isPayoutsRoute.value && merchantRouteId.value) {
      navigateToRoute('shopifyStoresPayouts')
    }

    if (props.merchantId) {
      store.syncMerchantId(props.merchantId)
    }

    if (!isPayoutDetailRoute.value) {
      store.setSelectedPayout(null)
    } else if (routePayoutId.value && !isBalanceTransactionDetailRoute.value) {
      await loadPayoutDetailForRoute(routePayoutId.value)
    }

    if (isBalanceTransactionDetailRoute.value) {
      await loadPayoutDetailForTransaction()
    }
  },
  { immediate: true },
)

watch(
  () => props.merchantId,
  (newMerchantId, oldMerchantId) => {
    if (
      newMerchantId &&
      newMerchantId !== oldMerchantId &&
      newMerchantId !== store.selectedMerchantId &&
      (props.visible || isPayoutsRoute.value)
    ) {
      store.syncMerchantId(newMerchantId)
    }
  },
)

watch(
  () => merchantRouteId.value,
  (newMerchantId, oldMerchantId) => {
    if (
      newMerchantId &&
      newMerchantId !== oldMerchantId &&
      newMerchantId !== store.selectedMerchantId &&
      isPayoutsRoute.value
    ) {
      store.syncMerchantId(newMerchantId)
    }
  },
)

watch(
  () => store.selectedMerchantId,
  newId => {
    selectedMerchantIdLocal.value = newId
  },
)

watch(
  () => isPendingPayoutsRoute.value,
  isPending => {
    if (isPending) {
      selectedMerchantIdLocal.value = '__PENDING__'
    }
  },
  { immediate: true },
)

watch(
  () => route.query.fromPending,
  fromPending => {
    if (fromPending === 'true') {
      cameFromPendingPayouts.value = true
    }
  },
  { immediate: true },
)

// Watch for merchant ID to become available and load balance transaction detail if needed
watch(
  () => effectiveMerchantId.value,
  async (newMerchantId, oldMerchantId) => {
    // Only trigger if merchant ID changed from null/undefined to a value
    // and we're on a balance transaction detail route
    if (
      newMerchantId &&
      !oldMerchantId &&
      isBalanceTransactionDetailRoute.value &&
      routePayoutId.value &&
      !store.selectedPayoutDetail
    ) {
      await loadPayoutDetailForTransaction()
    }
  },
)

// Lifecycle
onMounted(async () => {
  if (!isPayoutsRoute.value) return

  if (props.merchantId) {
    store.syncMerchantId(props.merchantId)
  }

  // Ensure merchant ID is available before proceeding
  if (!effectiveMerchantId.value) {
    return
  }

  // Handle direct URL access when payout is already in store
  if (isPayoutDetailRoute.value && routePayoutId.value) {
    const payout = store.findPayoutById(routePayoutId.value)
    if (payout) {
      store.setSelectedPayout(payout)
      try {
        await store.loadPayoutDetail(
          authState,
          effectiveMerchantId.value,
          payout.StoreId,
          payout.BusinessEntityId,
          payout.Id,
        )
      } catch (error) {
        handleApiError(error)
      }
    }
  }

  if (isBalanceTransactionDetailRoute.value) {
    await loadPayoutDetailForTransaction()
  }
})
</script>

<style scoped></style>
